import { useState, useEffect, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Progress } from '@/components/ui/progress'
import { GripVertical, Plus, Trash2, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { orpc } from '@/lib/orpc-client'

interface Subtask {
  id: string
  taskId: string
  title: string
  completed: boolean
  assigneeId: string | null
  position: number
  createdAt: string
}

interface Member {
  userId: string
  name: string | null
  avatarUrl: string | null
}

interface TaskSubtaskListProps {
  projectId: string
  taskId: string
  /** Parent task's assigned user IDs — subtasks can only pick from these */
  taskAssigneeIds: string[]
  members: Member[]
  /** Called when subtask counts change (for updating kanban card) */
  onProgressChange?: (total: number, completed: number) => void
}

// ─── Sortable subtask row ───────────────────────────────────────

function SortableSubtaskRow({
  subtask,
  taskAssigneeIds,
  members,
  onToggle,
  onUpdateTitle,
  onUpdateAssignee,
  onDelete,
}: {
  subtask: Subtask
  taskAssigneeIds: string[]
  members: Member[]
  onToggle: (id: string, completed: boolean) => void
  onUpdateTitle: (id: string, title: string) => void
  onUpdateAssignee: (id: string, assigneeId: string | null) => void
  onDelete: (id: string) => void
}) {
  const { t } = useTranslation('tasks')
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: subtask.id,
  })
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(subtask.title)
  const [assigneeOpen, setAssigneeOpen] = useState(false)

  const style = { transform: CSS.Transform.toString(transform), transition }

  const assignee = subtask.assigneeId ? members.find((m) => m.userId === subtask.assigneeId) : null

  const assignableMembers = members.filter((m) => taskAssigneeIds.includes(m.userId))

  const saveTitle = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== subtask.title) {
      onUpdateTitle(subtask.id, trimmed)
    } else {
      setEditValue(subtask.title)
    }
    setEditing(false)
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-1.5 group rounded-md px-1 py-0.5 hover:bg-accent/30',
        isDragging && 'opacity-50'
      )}
    >
      <button
        type="button"
        className="cursor-grab text-muted-foreground/50 hover:text-muted-foreground shrink-0"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3.5" />
      </button>

      <Checkbox
        checked={subtask.completed}
        onCheckedChange={(checked) => onToggle(subtask.id, checked === true)}
        className="shrink-0"
      />

      {editing ? (
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveTitle()
            if (e.key === 'Escape') {
              setEditValue(subtask.title)
              setEditing(false)
            }
          }}
          className="h-6 text-sm flex-1 px-1"
          autoFocus
        />
      ) : (
        <span
          className={cn(
            'text-sm flex-1 cursor-text truncate',
            subtask.completed && 'line-through text-muted-foreground'
          )}
          onClick={() => setEditing(true)}
        >
          {subtask.title}
        </span>
      )}

      {/* Subtask assignee */}
      {assignableMembers.length > 0 && (
        <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"
              title={assignee?.name ?? t('subtasks.assign')}
            >
              {assignee ? (
                <Avatar className="size-5">
                  {assignee.avatarUrl && <AvatarImage src={assignee.avatarUrl} />}
                  <AvatarFallback className="text-[8px]">
                    {(assignee.name ?? '?')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <User className="size-4 text-muted-foreground" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-1" align="end">
            <button
              type="button"
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-accent"
              onClick={() => {
                onUpdateAssignee(subtask.id, null)
                setAssigneeOpen(false)
              }}
            >
              <User className="size-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('subtasks.none')}</span>
            </button>
            {assignableMembers.map((m) => (
              <button
                key={m.userId}
                type="button"
                className={cn(
                  'flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-accent',
                  subtask.assigneeId === m.userId && 'bg-accent'
                )}
                onClick={() => {
                  onUpdateAssignee(subtask.id, m.userId)
                  setAssigneeOpen(false)
                }}
              >
                <Avatar className="size-5">
                  {m.avatarUrl && <AvatarImage src={m.avatarUrl} />}
                  <AvatarFallback className="text-[8px]">
                    {(m.name ?? '?')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{m.name ?? m.userId}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="size-6 opacity-0 group-hover:opacity-100 shrink-0"
        onClick={() => onDelete(subtask.id)}
        aria-label={t('subtasks.delete')}
      >
        <Trash2 className="size-3" />
      </Button>
    </div>
  )
}

// ─── Main component ─────────────────────────────────────────────

export function TaskSubtaskList({
  projectId,
  taskId,
  taskAssigneeIds,
  members,
  onProgressChange,
}: TaskSubtaskListProps) {
  const { t } = useTranslation('tasks')
  const [subtasks, setSubtasks] = useState<Subtask[]>([])
  const [newTitle, setNewTitle] = useState('')

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const notifyProgress = useCallback(
    (items: Subtask[]) => {
      if (!onProgressChange) return
      const total = items.length
      const completed = items.filter((s) => s.completed).length
      onProgressChange(total, completed)
    },
    [onProgressChange]
  )

  useEffect(() => {
    orpc.taskSubtasks
      .list({ projectId, taskId })
      .then((res) => {
        setSubtasks(res.items)
        notifyProgress(res.items)
      })
      .catch(() => {})
  }, [projectId, taskId])

  const handleCreate = async () => {
    const title = newTitle.trim()
    if (!title) return
    try {
      const subtask = await orpc.taskSubtasks.create({ projectId, taskId, title })
      const updated = [...subtasks, subtask]
      setSubtasks(updated)
      setNewTitle('')
      notifyProgress(updated)
    } catch {
      // ignore
    }
  }

  const handleToggle = async (id: string, completed: boolean) => {
    // Optimistic update
    const updated = subtasks.map((s) => (s.id === id ? { ...s, completed } : s))
    setSubtasks(updated)
    notifyProgress(updated)
    try {
      await orpc.taskSubtasks.update({ projectId, taskId, subtaskId: id, completed })
    } catch {
      // Revert
      setSubtasks(subtasks)
      notifyProgress(subtasks)
    }
  }

  const handleUpdateTitle = async (id: string, title: string) => {
    const updated = subtasks.map((s) => (s.id === id ? { ...s, title } : s))
    setSubtasks(updated)
    try {
      await orpc.taskSubtasks.update({ projectId, taskId, subtaskId: id, title })
    } catch {
      setSubtasks(subtasks)
    }
  }

  const handleUpdateAssignee = async (id: string, assigneeId: string | null) => {
    const updated = subtasks.map((s) => (s.id === id ? { ...s, assigneeId } : s))
    setSubtasks(updated)
    try {
      await orpc.taskSubtasks.update({ projectId, taskId, subtaskId: id, assigneeId })
    } catch {
      setSubtasks(subtasks)
    }
  }

  const handleDelete = async (id: string) => {
    const updated = subtasks.filter((s) => s.id !== id)
    setSubtasks(updated)
    notifyProgress(updated)
    try {
      await orpc.taskSubtasks.delete({ projectId, taskId, subtaskId: id })
    } catch {
      setSubtasks(subtasks)
      notifyProgress(subtasks)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = subtasks.findIndex((s) => s.id === active.id)
    const newIndex = subtasks.findIndex((s) => s.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = [...subtasks]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    setSubtasks(reordered)

    try {
      await orpc.taskSubtasks.reorder({
        projectId,
        taskId,
        subtaskIds: reordered.map((s) => s.id),
      })
    } catch {
      setSubtasks(subtasks)
    }
  }

  const completedCount = subtasks.filter((s) => s.completed).length
  const totalCount = subtasks.length
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  return (
    <div className="space-y-2">
      {/* Progress */}
      {totalCount > 0 && (
        <div className="flex items-center gap-2">
          <Progress value={progressPercent} className="h-1.5 flex-1" />
          <span className="text-xs text-muted-foreground shrink-0">
            {t('subtasks.progress', { completed: completedCount, total: totalCount })}
          </span>
        </div>
      )}

      {/* Subtask list with drag reorder */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={subtasks.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          {subtasks.map((subtask) => (
            <SortableSubtaskRow
              key={subtask.id}
              subtask={subtask}
              taskAssigneeIds={taskAssigneeIds}
              members={members}
              onToggle={handleToggle}
              onUpdateTitle={handleUpdateTitle}
              onUpdateAssignee={handleUpdateAssignee}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Add new subtask */}
      <div className="flex items-center gap-1.5">
        <Plus className="size-3.5 text-muted-foreground shrink-0" />
        <Input
          placeholder={t('subtasks.addPlaceholder')}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleCreate()
            }
          }}
          className="h-7 text-sm flex-1"
        />
      </div>
    </div>
  )
}
